/**
 * @brouhaha/errors
 *
 * Errors go to Sentry (tech stack doc, observability). This is not the
 * Sentry SDK: that is tens of kilobytes the scan route cannot afford, and
 * the lobby worker needs no browser features. It is the envelope protocol,
 * by hand: parse a DSN, build one event, post it. Every event is tagged
 * with lobby id, game session id and game version when they are known
 * (CLAUDE.md), and never carries a nickname.
 *
 * Without a DSN, `report` is a no-op. A failed send is swallowed: telemetry
 * must never break a game.
 */

export interface Dsn {
  publicKey: string;
  host: string;
  projectId: string;
  /** https://host/api/<project>/envelope/ */
  envelopeUrl: string;
}

export function parseDsn(dsn: string): Dsn | null {
  let url: URL;
  try {
    url = new URL(dsn);
  } catch {
    return null;
  }
  const projectId = url.pathname.replace(/^\/+|\/+$/g, "");
  if (!url.username || !projectId || !url.host) return null;
  return {
    publicKey: url.username,
    host: url.host,
    projectId,
    envelopeUrl: `${url.protocol}//${url.host}/api/${projectId}/envelope/`,
  };
}

export interface ErrorContext {
  /** "web" or "lobby". */
  service: string;
  environment?: string;
  release?: string;
  tags?: Record<string, string | number | null | undefined>;
  extra?: Record<string, unknown>;
}

export interface SentryEvent {
  event_id: string;
  timestamp: number;
  platform: "javascript";
  level: "error";
  environment?: string;
  release?: string;
  tags: Record<string, string>;
  exception: { values: Array<{ type: string; value: string; stacktrace?: { frames: Frame[] } }> };
  extra?: Record<string, unknown>;
}

export interface Frame {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
}

/** Enough of a V8 or Firefox stack to be useful, oldest frame first as Sentry wants. */
export function parseStack(stack: string | undefined): Frame[] {
  if (!stack) return [];
  const frames: Frame[] = [];
  for (const line of stack.split("\n")) {
    const v8 = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/.exec(line);
    const ff = /^(.*?)@(.+?):(\d+):(\d+)$/.exec(line.trim());
    const m = v8 ?? ff;
    if (!m) continue;
    const [, fn, filename, lineno, colno] = m;
    frames.push({
      ...(fn ? { function: fn } : {}),
      ...(filename ? { filename } : {}),
      lineno: Number(lineno),
      colno: Number(colno),
    });
  }
  return frames.reverse();
}

function describe(error: unknown): { type: string; value: string; stack?: string } {
  if (error instanceof Error) {
    return {
      type: error.name || "Error",
      value: error.message,
      ...(error.stack ? { stack: error.stack } : {}),
    };
  }
  if (typeof error === "string") return { type: "Error", value: error };
  return { type: "Error", value: safeString(error) };
}

function safeString(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

export function buildEvent(
  error: unknown,
  ctx: ErrorContext,
  eventId: string,
  now: number,
): SentryEvent {
  const { type, value, stack } = describe(error);
  const tags: Record<string, string> = { service: ctx.service };
  for (const [k, v] of Object.entries(ctx.tags ?? {})) {
    if (v !== null && v !== undefined && v !== "") tags[k] = String(v);
  }
  const frames = parseStack(stack);
  return {
    event_id: eventId,
    timestamp: now / 1000,
    platform: "javascript",
    level: "error",
    ...(ctx.environment ? { environment: ctx.environment } : {}),
    ...(ctx.release ? { release: ctx.release } : {}),
    tags,
    exception: { values: [{ type, value, ...(frames.length ? { stacktrace: { frames } } : {}) }] },
    ...(ctx.extra || stack ? { extra: { ...(stack ? { stack } : {}), ...ctx.extra } } : {}),
  };
}

/** Three newline-separated JSON lines: the envelope header, the item header, the event. */
export function buildEnvelope(event: SentryEvent, dsn: Dsn, now: number): string {
  const header = {
    event_id: event.event_id,
    sent_at: new Date(now).toISOString(),
    dsn: `https://${dsn.publicKey}@${dsn.host}/${dsn.projectId}`,
  };
  return `${JSON.stringify(header)}\n${JSON.stringify({ type: "event" })}\n${JSON.stringify(event)}\n`;
}

export function authHeader(dsn: Dsn, client = "brouhaha-errors/0.1"): string {
  return `Sentry sentry_version=7, sentry_client=${client}, sentry_key=${dsn.publicKey}`;
}

export function randomEventId(random: () => number = Math.random): string {
  let out = "";
  for (let i = 0; i < 32; i++) out += Math.floor(random() * 16).toString(16);
  return out;
}

export interface Reporter {
  /** Sends one error. Never throws; returns the send, for a worker's waitUntil. */
  report(error: unknown, ctx?: Partial<ErrorContext>): Promise<void>;
  readonly enabled: boolean;
}

export interface ReporterOptions {
  dsn: string | undefined;
  service: string;
  environment?: string;
  release?: string;
  fetch: typeof fetch;
  now?: () => number;
  random?: () => number;
}

export function createReporter(options: ReporterOptions): Reporter {
  const dsn = options.dsn ? parseDsn(options.dsn) : null;
  const now = options.now ?? Date.now;
  if (!dsn) return { enabled: false, report: async () => {} };
  return {
    enabled: true,
    async report(error, ctx = {}) {
      try {
        const at = now();
        const event = buildEvent(
          error,
          {
            service: ctx.service ?? options.service,
            ...(options.environment ? { environment: options.environment } : {}),
            ...(options.release ? { release: options.release } : {}),
            ...(ctx.tags ? { tags: ctx.tags } : {}),
            ...(ctx.extra ? { extra: ctx.extra } : {}),
          },
          randomEventId(options.random),
          at,
        );
        await options.fetch(dsn.envelopeUrl, {
          method: "POST",
          headers: {
            "content-type": "application/x-sentry-envelope",
            "x-sentry-auth": authHeader(dsn),
          },
          body: buildEnvelope(event, dsn, at),
        });
      } catch {
        // Telemetry never breaks a game.
      }
    },
  };
}
